import { fetchClient } from "@/api/api";
import type {
	UserLoginIn,
	loginOutput,
	logoutOutput,
} from "@/api/custom_types";
import { useApiError } from "@/hooks";
import { currentUserAtom } from "@/store/login";
import type { CustomMutationHook } from "@/utils/react-query";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { AUTH_SERVICE_KEYS } from "./keys";

import { useTranslation } from "@/hooks";
import { queryClient } from "@/utils/queryClient";
import { toast } from "react-toastify";

class NoPermissionError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "NoPermissionError";
	}
}

const useLogin: CustomMutationHook<UserLoginIn, loginOutput> = () => {
	const setCurrentUser = useSetAtom(currentUserAtom);
	const { toastApiError } = useApiError();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: AUTH_SERVICE_KEYS.useLogin(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST("/v1/marketplace/login", {
				body,
				throwOnError: true,
			});
			if (error) throw error;
			if (data && data.user_role === "FC_USER") {
				throw new NoPermissionError(
					"You do not have access to this application.",
				);
			}
			return data;
		},
		onSuccess: (response) => {
			setCurrentUser(response);
		},
		onError(error) {
			if (error instanceof NoPermissionError) {
				toast.error(t("auth.accessDenied"));
			} else toastApiError(error);
		},
	});
};

const useLogout: CustomMutationHook<void, logoutOutput> = () => {
	const setCurrentUser = useSetAtom(currentUserAtom);
	const navigate = useNavigate();
	const { t } = useTranslation();

	return useMutation({
		mutationKey: AUTH_SERVICE_KEYS.useLogout(),
		mutationFn: async () => {
			const { data, error } = await fetchClient.POST("/v1/marketplace/logout", {
				throwOnError: true,
			});
			if (error) throw error;
			return data;
		},
		onSuccess: () => {
			toast.success(t("auth.successfullyLogout"));
			queryClient.clear();
			navigate({ to: "/login" });
			setCurrentUser(null);
		},
	});
};

export { useLogin, useLogout };
